import React from 'react';
import { useTranslation } from 'react-i18next';
import { CentralUsageCard } from './components/CentralUsageCard/CentralUsageCard';
import centralLimitsService from '../../core/services/CentralLimitsService';
import { CENTRAL_EVENTS } from './constants/events';

interface CentralMetricsState {
  volume: number;
  limit: number;
  hasUsergroupLimits: boolean;
  isLoading: boolean;
}

const INITIAL_STATE: CentralMetricsState = {
  volume: 0,
  limit: 0,
  hasUsergroupLimits: false,
  isLoading: true,
};

export const CentralApp: React.FC = () => {
  const { t } = useTranslation();
  const [isExternallyVisible, setIsExternallyVisible] = React.useState(true);
  const [metrics, setMetrics] = React.useState<CentralMetricsState>(INITIAL_STATE);
  const requestIdRef = React.useRef(0);

  const loadMetrics = React.useCallback(async (accountId?: string) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    document.dispatchEvent(
      new CustomEvent(CENTRAL_EVENTS.LOADING, {
        bubbles: true,
        composed: true,
      }),
    );

    setMetrics((current) => ({
      ...current,
      isLoading: true,
    }));

    try {
      const response = await centralLimitsService.getLimitByAccountId(accountId);
      
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const usergroupLimits = response?.usergroup_limits;

      if (!usergroupLimits) {
        setMetrics({
          volume: 0,
          limit: 0,
          hasUsergroupLimits: false,
          isLoading: false,
        });

        document.dispatchEvent(
          new CustomEvent(CENTRAL_EVENTS.LOADED, {
            detail: { hasUsergroupLimits: false },
            bubbles: true,
            composed: true,
          }),
        );

        return;
      }

      const volume =
        (usergroupLimits.total_month_listening_posts ?? 0) +
        (usergroupLimits.total_month_proprietary_posts ?? 0);
      const limit = usergroupLimits.month_posts_limit ?? 0;

      setMetrics({
        volume,
        limit,
        hasUsergroupLimits: true,
        isLoading: false,
      });

      document.dispatchEvent(
        new CustomEvent(CENTRAL_EVENTS.LOADED, {
          detail: {
            hasUsergroupLimits: true,
            volume,
            limit,
          },
          bubbles: true,
          composed: true,
        }),
      );
    } catch {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setMetrics({
        volume: 0,
        limit: 0,
        hasUsergroupLimits: false,
        isLoading: false,
      });

      document.dispatchEvent(
        new CustomEvent(CENTRAL_EVENTS.ERROR_LOADING, {
          bubbles: true,
          composed: true,
        }),
      );
    }
  }, []);

  React.useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  React.useEffect(() => {
    const handleShow = () => setIsExternallyVisible(true);
    const handleHide = () => setIsExternallyVisible(false);
    const handleRefresh = (event: Event) => {
      const refreshEvent = event as CustomEvent<{ accountId?: string | number }>;
      const accountId = refreshEvent.detail?.accountId
        ? String(refreshEvent.detail.accountId)
        : undefined;
      void loadMetrics(accountId);
    };

    document.addEventListener(CENTRAL_EVENTS.SHOW, handleShow);
    document.addEventListener(CENTRAL_EVENTS.HIDE, handleHide);
    document.addEventListener(CENTRAL_EVENTS.REFRESH, handleRefresh);

    return () => {
      document.removeEventListener(CENTRAL_EVENTS.SHOW, handleShow);
      document.removeEventListener(CENTRAL_EVENTS.HIDE, handleHide);
      document.removeEventListener(CENTRAL_EVENTS.REFRESH, handleRefresh);
    };
  }, [loadMetrics]);

  const shouldShowCounter = isExternallyVisible && metrics.hasUsergroupLimits;

  React.useEffect(() => {
    document.dispatchEvent(
      new CustomEvent(CENTRAL_EVENTS.VISIBILITY_CHANGE, {
        detail: { isVisible: shouldShowCounter },
        bubbles: true,
        composed: true,
      }),
    );
  }, [shouldShowCounter]);

  if (!metrics.isLoading && !shouldShowCounter) {
    return null;
  }

  return (
    <div className="central-app-list">
      {metrics.isLoading ? (
        <span className="central-app-list__loading">{t('loading')}</span>
      ) : (
        <CentralUsageCard volume={metrics.volume} limit={metrics.limit} />
      )}
    </div>
  );
};
